import React, { useState, useEffect } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import {
  imagesOutline,
  timeOutline,
  shuffleOutline,
  layersOutline,
  cloudOutline,
  folderOutline,
  refreshOutline,
  checkmarkCircle,
} from 'ionicons/icons';
import {
  Modal,
  ModalSection,
  ModalRow,
  ModalActionRow,
} from './ui/Modal/Modal';
import { type SlideshowConfig } from '../services/dashboard';
import { fetchGooglePhotoAlbums, isGoogleConnected } from '../services/google';

interface SlideshowConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  config: SlideshowConfig | null;
  onSave: (config: SlideshowConfig) => void;
  householdId: string;
  /** Local photo albums from the database */
  localAlbums?: Array<{ id: string; name: string; photo_count?: number }>;
}

interface GoogleAlbum {
  id: string;
  title: string;
  mediaItemsCount?: string;
  coverPhotoBaseUrl?: string;
}

const DEFAULT_CONFIG: SlideshowConfig = {
  source: 'local',
  interval_seconds: 30,
  transition: 'crossfade',
  shuffle: true,
  preload_count: 3,
};

const SlideshowConfigModal: React.FC<SlideshowConfigModalProps> = ({
  isOpen,
  onClose,
  config,
  onSave,
  householdId,
  localAlbums = [],
}) => {
  const [formConfig, setFormConfig] = useState<SlideshowConfig>(config || DEFAULT_CONFIG);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleAlbums, setGoogleAlbums] = useState<GoogleAlbum[]>([]);
  const [loadingAlbums, setLoadingAlbums] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormConfig(config || DEFAULT_CONFIG);
      checkGoogleConnection();
    }
  }, [isOpen, config]);

  // Check if Google is connected
  const checkGoogleConnection = async () => {
    const connected = await isGoogleConnected(householdId);
    setGoogleConnected(connected);
    if (connected && formConfig.source === 'google_photos') {
      loadGoogleAlbums();
    }
  };

  // Load Google Photos albums
  const loadGoogleAlbums = async () => {
    setLoadingAlbums(true);
    try {
      const albums = await fetchGooglePhotoAlbums(householdId);
      setGoogleAlbums(albums || []);
    } catch (err) {
      console.error('Failed to load Google albums:', err);
    } finally {
      setLoadingAlbums(false);
    }
  };

  // Handle source change
  const handleSourceChange = (source: 'local' | 'google_photos') => {
    setFormConfig(prev => ({
      ...prev,
      source,
      album_id: undefined, // Clear album selection when source changes
    }));
    if (source === 'google_photos' && googleConnected && googleAlbums.length === 0) {
      loadGoogleAlbums();
    }
  };

  // Handle save
  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(formConfig);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Get current album name
  const getCurrentAlbumName = () => {
    if (!formConfig.album_id) return 'Select an album';

    if (formConfig.source === 'google_photos') {
      const album = googleAlbums.find(a => a.id === formConfig.album_id);
      return album?.title || 'Unknown album';
    } else {
      const album = localAlbums.find(a => a.id === formConfig.album_id);
      return album?.name || 'Unknown album';
    }
  };

  // Interval options
  const intervalOptions = [
    { value: 10, label: '10 seconds' },
    { value: 15, label: '15 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 120, label: '2 minutes' },
    { value: 300, label: '5 minutes' },
  ];

  // Transition options
  const transitionOptions = [
    { value: 'fade', label: 'Fade' },
    { value: 'crossfade', label: 'Crossfade' },
    { value: 'slide', label: 'Slide' },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={{ title: 'Slideshow Settings' }}
      footer={{
        primaryAction: {
          label: 'Save',
          onClick: handleSave,
          loading: saving,
          disabled: !formConfig.album_id,
        },
        secondaryAction: {
          label: 'Cancel',
          onClick: onClose,
        },
      }}
    >
      {/* Photo Source */}
      <ModalSection title="Photo Source">
        <div style={{ display: 'flex', gap: 12 }}>
          <SourceButton
            icon={folderOutline}
            label="Local Albums"
            selected={formConfig.source === 'local'}
            onClick={() => handleSourceChange('local')}
          />
          <SourceButton
            icon={cloudOutline}
            label="Google Photos"
            selected={formConfig.source === 'google_photos'}
            onClick={() => handleSourceChange('google_photos')}
            disabled={!googleConnected}
            disabledText="Not connected"
          />
        </div>
      </ModalSection>

      {/* Album Selection */}
      <ModalSection title="Album">
        {formConfig.source === 'google_photos' ? (
          <>
            {loadingAlbums ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 0' }}>
                <IonSpinner name="crescent" style={{ width: 20, height: 20 }} />
                <span style={{ color: 'var(--ion-color-medium)' }}>Loading albums...</span>
              </div>
            ) : googleAlbums.length > 0 ? (
              <AlbumGrid
                albums={googleAlbums.map(a => ({
                  id: a.id,
                  name: a.title,
                  count: parseInt(a.mediaItemsCount || '0'),
                  thumbnail: a.coverPhotoBaseUrl ? `${a.coverPhotoBaseUrl}=w200-h200` : undefined,
                }))}
                selectedId={formConfig.album_id}
                onSelect={(id) => setFormConfig(prev => ({ ...prev, album_id: id }))}
              />
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '24px 16px',
                color: 'var(--ion-color-medium)',
              }}>
                <p>No albums found in Google Photos</p>
                <button
                  onClick={loadGoogleAlbums}
                  style={{
                    marginTop: 12,
                    padding: '8px 16px',
                    border: '1px solid var(--ion-border-color)',
                    borderRadius: 8,
                    background: 'transparent',
                    color: 'var(--ion-text-color)',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <IonIcon icon={refreshOutline} />
                  Refresh
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {localAlbums.length > 0 ? (
              <AlbumGrid
                albums={localAlbums.map(a => ({
                  id: a.id,
                  name: a.name,
                  count: a.photo_count,
                }))}
                selectedId={formConfig.album_id}
                onSelect={(id) => setFormConfig(prev => ({ ...prev, album_id: id }))}
              />
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '24px 16px',
                color: 'var(--ion-color-medium)',
              }}>
                <p>No local photo albums found</p>
                <p style={{ fontSize: 13, marginTop: 8 }}>
                  Create a photo album in Settings to use local photos
                </p>
              </div>
            )}
          </>
        )}
      </ModalSection>

      {/* Timing */}
      <ModalSection title="Timing">
        <ModalRow icon={timeOutline} label="Interval">
          <select
            value={formConfig.interval_seconds}
            onChange={(e) => setFormConfig(prev => ({
              ...prev,
              interval_seconds: parseInt(e.target.value)
            }))}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--ion-border-color)',
              background: 'var(--ion-card-background)',
              color: 'var(--ion-text-color)',
              fontSize: 14,
            }}
          >
            {intervalOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </ModalRow>
      </ModalSection>

      {/* Transition */}
      <ModalSection title="Transition">
        <ModalRow icon={layersOutline} label="Effect">
          <select
            value={formConfig.transition}
            onChange={(e) => setFormConfig(prev => ({
              ...prev,
              transition: e.target.value as SlideshowConfig['transition']
            }))}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid var(--ion-border-color)',
              background: 'var(--ion-card-background)',
              color: 'var(--ion-text-color)',
              fontSize: 14,
            }}
          >
            {transitionOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </ModalRow>
      </ModalSection>

      {/* Options */}
      <ModalSection title="Options" divider={false}>
        <ModalActionRow
          icon={shuffleOutline}
          iconColor="var(--ion-color-primary)"
          title="Shuffle Photos"
          subtitle="Randomize the order of photos"
          toggle
          checked={formConfig.shuffle}
          onToggle={(checked) => setFormConfig(prev => ({ ...prev, shuffle: checked }))}
        />
      </ModalSection>
    </Modal>
  );
};

// Source button component
interface SourceButtonProps {
  icon: string;
  label: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
  disabledText?: string;
}

const SourceButton: React.FC<SourceButtonProps> = ({
  icon,
  label,
  selected,
  onClick,
  disabled,
  disabledText,
}) => (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      flex: 1,
      padding: '16px 12px',
      borderRadius: 12,
      border: selected
        ? '2px solid var(--ion-color-primary)'
        : '2px solid var(--ion-border-color)',
      background: selected
        ? 'rgba(var(--ion-color-primary-rgb), 0.1)'
        : 'transparent',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 8,
      transition: 'all 0.15s ease',
    }}
  >
    <IonIcon
      icon={icon}
      style={{
        fontSize: 24,
        color: selected ? 'var(--ion-color-primary)' : 'var(--ion-color-medium)'
      }}
    />
    <span style={{
      fontSize: 13,
      fontWeight: selected ? 600 : 500,
      color: selected ? 'var(--ion-text-color)' : 'var(--ion-color-medium)',
    }}>
      {label}
    </span>
    {disabled && disabledText && (
      <span style={{
        fontSize: 11,
        color: 'var(--ion-color-danger)',
      }}>
        {disabledText}
      </span>
    )}
  </button>
);

// Album grid component
interface AlbumGridProps {
  albums: Array<{
    id: string;
    name: string;
    count?: number;
    thumbnail?: string;
  }>;
  selectedId?: string;
  onSelect: (id: string) => void;
}

const AlbumGrid: React.FC<AlbumGridProps> = ({ albums, selectedId, onSelect }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
    gap: 12,
    maxHeight: 240,
    overflowY: 'auto',
    padding: '4px 0',
  }}>
    {albums.map(album => (
      <button
        key={album.id}
        onClick={() => onSelect(album.id)}
        style={{
          padding: 0,
          borderRadius: 10,
          border: selectedId === album.id
            ? '2px solid var(--ion-color-primary)'
            : '2px solid var(--ion-border-color)',
          background: 'var(--ion-card-background)',
          cursor: 'pointer',
          overflow: 'hidden',
          transition: 'all 0.15s ease',
          position: 'relative',
        }}
      >
        {/* Thumbnail or placeholder */}
        <div style={{
          width: '100%',
          aspectRatio: '1',
          background: album.thumbnail
            ? `url(${album.thumbnail}) center/cover`
            : 'var(--ion-color-step-100)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {!album.thumbnail && (
            <IonIcon
              icon={imagesOutline}
              style={{ fontSize: 32, color: 'var(--ion-color-step-300)' }}
            />
          )}
        </div>

        {/* Album info */}
        <div style={{
          padding: '8px 10px',
          textAlign: 'left',
        }}>
          <div style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--ion-text-color)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {album.name}
          </div>
          {album.count !== undefined && (
            <div style={{
              fontSize: 11,
              color: 'var(--ion-color-medium)',
              marginTop: 2,
            }}>
              {album.count} photos
            </div>
          )}
        </div>

        {/* Selected indicator */}
        {selectedId === album.id && (
          <div style={{
            position: 'absolute',
            top: 8,
            right: 8,
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: 'var(--ion-color-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <IonIcon
              icon={checkmarkCircle}
              style={{ fontSize: 16, color: '#fff' }}
            />
          </div>
        )}
      </button>
    ))}
  </div>
);

export default SlideshowConfigModal;
